import { useEffect, useMemo } from "react";
import type {
	EngagementProvider,
	EngagementProviderEvent,
} from "@/provider/EngagementProvider";
import { FeatureEventsBus } from "@/features/runtime/FeatureEvents";
import type {
	EFFeatureRewardAcquiredHandler,
} from "@/features/runtime/FeatureTypes";
import { debugLog } from "@/utils/debug";

export type UseFeatureEventsResult = {
	subscribeRewardAcquired: (
		handler: EFFeatureRewardAcquiredHandler
	) => () => void;
	getSnapshot: () => ReturnType<FeatureEventsBus["getSnapshot"]>;
};

export function useFeatureEvents(
	provider: EngagementProvider | null,
	providerReady: boolean
): UseFeatureEventsResult {
	const bus = useMemo(() => new FeatureEventsBus(), []);

	useEffect(() => {
		if (!provider || !providerReady) {
			debugLog(
				"[FeatureEvents] provider reward subscription skipped because provider is not ready",
				{
					hasProvider: !!provider,
					providerReady,
				}
			);
			return;
		}

		debugLog("[FeatureEvents] subscribing to normalized provider events", {
			providerId: provider.id,
			providerReady,
		});

		const unsubscribe = provider.subscribe((event: EngagementProviderEvent) => {
			if (event.type !== "reward_acquired") {
				return;
			}

			if (event.rewardKeys.length === 0) {
				return;
			}

			debugLog("[FeatureEvents] reward_acquired received from provider", {
				providerId: provider.id,
				rewardKeys: event.rewardKeys,
			});

			bus.emitRewardAcquired({
				rewardKeys: event.rewardKeys,
			});
		});

		return () => {
			debugLog(
				"[FeatureEvents] unsubscribing from normalized provider events",
				{
					providerId: provider.id,
				}
			);

			unsubscribe();
		};
	}, [bus, provider, providerReady]);

	return useMemo<UseFeatureEventsResult>(
		() => ({
			subscribeRewardAcquired: (
				handler: EFFeatureRewardAcquiredHandler
			) => bus.subscribeRewardAcquired(handler),
			getSnapshot: () => bus.getSnapshot(),
		}),
		[bus]
	);
}

export default useFeatureEvents;